import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Users,
  Gift,
  Copy,
  Check,
  Share2,
  Clock,
  Award,
  ChevronRight,
  ExternalLink,
  Loader2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralDashboard() {
  useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/referral/dashboard`);
      setData(res.data);
    } catch (err) {
      console.error('Error fetching referral data:', err);
      toast.error('Erreur lors du chargement des données de parrainage');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const copyCode = () => {
    if (!data?.stats?.referral_code) return;
    navigator.clipboard.writeText(data.stats.referral_code);
    setCopied(true);
    toast.success('Code copié !');
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    if (!data?.stats?.referral_link) return;
    navigator.clipboard.writeText(data.stats.referral_link);
    setCopiedLink(true);
    toast.success('Lien copié !');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  if (loading) {
    return (
      <DashboardLayout title="Parrainage">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  const stats = data?.stats;
  const referrals = data?.referrals || [];
  const ownReferrer = data?.own_referrer;

  return (
    <DashboardLayout title="Parrainage">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-white" data-testid="referral-header">
          <div className="flex items-center gap-3 mb-2">
            <Gift className="w-7 h-7" />
            <h1 className="text-xl font-bold">Programme de parrainage</h1>
          </div>
          <p className="text-emerald-100 text-sm">
            Parrainez d'autres commerces et gagnez 1 mois de frais offerts pour chaque filleul atteignant 10 commandes.
          </p>
        </div>

        {/* Own Referrer Banner */}
        {ownReferrer && (
          <Card className="border-blue-200 bg-blue-50" data-testid="own-referrer-banner">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Award className="w-5 h-5 text-blue-600 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">
                    Vous avez ete parraine par <strong>{ownReferrer.restaurant_name}</strong>
                  </p>
                  {ownReferrer.is_activated ? (
                    <p className="text-xs text-blue-600 mt-1">Bonus active - 1 mois offert credite !</p>
                  ) : (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-blue-700 mb-1">
                        <span>{ownReferrer.orders_count}/{ownReferrer.orders_target} commandes</span>
                        <span>{Math.round((ownReferrer.orders_count / ownReferrer.orders_target) * 100)}%</span>
                      </div>
                      <Progress 
                        value={(ownReferrer.orders_count / ownReferrer.orders_target) * 100} 
                        className="h-2"
                      />
                      <p className="text-xs text-blue-600 mt-1">
                        Encore {ownReferrer.orders_target - ownReferrer.orders_count} commande(s) pour debloquer votre mois offert
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="stat-total-referrals">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Filleuls</CardTitle>
              <Users className="w-5 h-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_referrals || 0}</div>
              <p className="text-xs text-gray-500 mt-1">
                {stats?.remaining_slots || 0} place(s) restante(s)
              </p>
            </CardContent>
          </Card>

          <Card data-testid="stat-active-referrals">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Actifs</CardTitle>
              <Check className="w-5 h-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">{stats?.active_referrals || 0}</div>
              <p className="text-xs text-gray-500 mt-1">Ont atteint 10 commandes</p>
            </CardContent>
          </Card>

          <Card data-testid="stat-pending-referrals">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">En attente</CardTitle>
              <Clock className="w-5 h-5 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-500">{stats?.pending_referrals || 0}</div>
              <p className="text-xs text-gray-500 mt-1">En cours d'activation</p>
            </CardContent>
          </Card>

          <Card data-testid="stat-credits">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Credits gagnes</CardTitle>
              <Gift className="w-5 h-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{stats?.credits_available_months || 0}</div>
              <p className="text-xs text-gray-500 mt-1">Mois de frais offerts</p>
            </CardContent>
          </Card>
        </div>

        {/* Referral Code & Link */}
        <Card data-testid="referral-code-card">
          <CardHeader>
            <CardTitle className="text-base">Votre code de parrainage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Code */}
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                <span className="text-2xl font-bold tracking-widest text-gray-900" data-testid="referral-code">
                  {stats?.referral_code || '...'}
                </span>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={copyCode}
                className="shrink-0 h-12 w-12 rounded-xl"
                data-testid="copy-referral-code"
              >
                {copied ? <Check className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5" />}
              </Button>
            </div>

            {/* Link */}
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 truncate">
                <span className="text-sm text-gray-600" data-testid="referral-link">
                  {stats?.referral_link || '...'}
                </span>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={copyLink}
                className="shrink-0 h-10 w-10 rounded-xl"
                data-testid="copy-referral-link"
              >
                {copiedLink ? <Check className="w-4 h-4 text-emerald-500" /> : <ExternalLink className="w-4 h-4" />}
              </Button>
            </div>

            {/* Share Button */}
            {typeof navigator.share === 'function' && (
              <Button
                onClick={() => {
                  navigator.share({
                    title: 'Rejoignez LEMO !',
                    text: `Utilisez mon code ${stats?.referral_code} pour vous inscrire sur LEMO et beneficier d'1 mois de frais offerts !`,
                    url: stats?.referral_link
                  }).catch(() => {});
                }}
                className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-xl"
                data-testid="share-referral"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Partager mon lien de parrainage
              </Button>
            )}
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card data-testid="how-it-works">
          <CardHeader>
            <CardTitle className="text-base">Comment ca marche ?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Step num={1} title="Partagez votre code" desc="Envoyez votre code ou lien a un commercant interesse." />
              <Step num={2} title="Inscription du filleul" desc="Le commercant s'inscrit sur LEMO avec votre code." />
              <Step num={3} title="10 commandes completees" desc="Le filleul atteint 10 commandes sur la plateforme." />
              <Step num={4} title="Bonus active !" desc="Vous et votre filleul recevez chacun 1 mois de frais offerts." />
            </div>
          </CardContent>
        </Card>

        {/* Referrals List */}
        <Card data-testid="referrals-list">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Mes filleuls ({referrals.length}/5)</span>
              {stats?.remaining_slots === 0 && (
                <Badge variant="secondary" className="text-xs">Maximum atteint</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {referrals.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Aucun filleul pour le moment</p>
                <p className="text-gray-400 text-xs mt-1">Partagez votre code pour commencer</p>
              </div>
            ) : (
              <div className="space-y-3">
                {referrals.map((ref) => (
                  <ReferralItem key={ref.restaurant_id} referral={ref} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function Step({ num, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
        <span className="text-sm font-bold text-emerald-600">{num}</span>
      </div>
      <div>
        <p className="font-medium text-gray-900 text-sm">{title}</p>
        <p className="text-xs text-gray-500">{desc}</p>
      </div>
    </div>
  );
}

function ReferralItem({ referral }) {
  const progressPct = Math.min(100, (referral.orders_count / referral.orders_target) * 100);

  return (
    <div
      className={`p-4 rounded-xl border ${
        referral.is_active ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-white'
      }`}
      data-testid={`referral-item-${referral.restaurant_id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="font-medium text-gray-900 text-sm">{referral.restaurant_name}</p>
          <p className="text-xs text-gray-500">{referral.owner_name}</p>
        </div>
        {referral.is_active ? (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>
        ) : (
          <Badge className="bg-orange-100 text-orange-700 border-orange-200">En attente</Badge>
        )}
      </div>

      {!referral.is_active && (
        <div className="mt-2">
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
            <span>{referral.orders_count}/{referral.orders_target} commandes</span>
            <span>{Math.round(progressPct)}%</span>
          </div>
          <Progress value={progressPct} className="h-1.5" />
        </div>
      )}

      {referral.is_active && referral.activated_at && (
        <p className="text-xs text-emerald-600 mt-1">
          Active le {new Date(referral.activated_at).toLocaleDateString('fr-FR')}
        </p>
      )}
    </div>
  );
}
